import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function TermsOfUse() {
  const terms = [
    {
      number: '1',
      title: 'Accurate Registration Information',
      description: 'Users must provide accurate, complete, and up-to-date registration information when creating an account on the Ami Bites platform.'
    },
    {
      number: '2',
      title: 'Approved Payment Methods',
      description: 'All payments must be made using approved payment methods as specified on the platform. Users are responsible for ensuring their payment information is valid and current.'
    },
    {
      number: '3',
      title: 'Lawful Use Only',
      description: 'The platform must be used for lawful purposes only. Users agree not to engage in any activity that violates local, national, or international laws.'
    },
    {
      number: '4',
      title: 'Delivery Time Acknowledgment',
      description: 'Users acknowledge that delivery times may vary due to operational factors including but not limited to traffic conditions, weather, restaurant preparation times, and order volume.'
    }
  ]

  const reservedRights = [
    'Update platform policies',
    'Suspend accounts for misuse',
    'Modify service fees where applicable'
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <div className="pt-20">
      <SEO 
        title="Terms of Use - User Agreement & Platform Policies | Ami Bites"
        description="Read Ami Bites terms of use and platform policies. Learn about user responsibilities, payment methods, delivery terms, and service conditions for our food delivery platform."
        keywords="terms of use, user agreement, platform policies, service terms, food delivery terms, Ami Bites policies, user responsibilities"
        canonical="https://amibites.com/terms-of-use"
      />
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl mb-6"
            >
              <i className="ri-file-text-line text-4xl text-white"></i>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Terms <span className="text-gradient">of Use</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              By accessing and using the Ami Bites platform, you agree to the following terms and conditions
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            {terms.map((term, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all group"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <motion.div
                      initial={{ rotate: -180, scale: 0 }}
                      whileInView={{ rotate: 0, scale: 1 }}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                      className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center"
                    >
                      <span className="text-white font-bold text-xl">{term.number}</span>
                    </motion.div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold mb-3 text-gray-900 flex items-center">
                      {term.title}
                      <i className="ri-checkbox-circle-line text-2xl text-orange-500 ml-2"></i>
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {term.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-12 bg-white rounded-2xl p-8 shadow-lg"
          >
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0">
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center"
                >
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </motion.div>
              </div>
              <div className="flex-grow">
                <h3 className="text-2xl font-bold mb-3 text-gray-900">Ami Bites Reserved Rights</h3>
                <p className="text-gray-600 leading-relaxed text-lg mb-4">
                  Ami Bites reserves the right to:
                </p>
                <ul className="space-y-2">
                  {reservedRights.map((right, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-line text-xl text-green-500 flex-shrink-0 mt-1"></i>
                      <span className="text-gray-700 text-lg">{right}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-8 p-8 bg-gradient-to-br from-orange-50 to-green-50 rounded-2xl border-l-4 border-orange-600"
          >
            <h3 className="font-bold text-xl mb-3 text-gradient">Agreement to Terms</h3>
            <p className="text-gray-700 leading-relaxed">
              By using the Ami Bites platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use. If you do not agree to these terms, please discontinue use of the platform immediately.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-8 text-center text-gray-500 text-sm"
          >
            <p>Last updated: February 2026</p>
            <p className="mt-2">© 2026 Ami Bites. A member of the Pitch For Me Group of Companies.</p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
